import { cpuName, type CpuDifficulty } from 'features/battle/ai-config';
import { autoConcedeLoser } from 'features/battle/emote-config';
import {
  BADGE_STAT_KEYS,
  BADGE_STAT_LABELS,
  BATTLE_BADGE_IDS,
  ZERO_BADGE_STATS,
  type BadgeStatKey,
  type BadgeStats,
} from 'features/catalog/badge-definitions';
import { RARITY_ORDER, RARITY_TIER_LABELS, rarityOrderIndex } from 'features/catalog/rarity';
import type { RarityName } from 'features/catalog/rarity-config';
import {
  badgeRarity,
  cardEffects,
  cardLegalPiles,
  cardName,
  cardRarity,
  cardStatGain,
  cardStats,
  cardTotalStats,
  effectDoesNothing,
  battleFirstPile,
  battlePileCount,
  canRemoveLeftmostPile,
  cpuDrives,
  effectRequiresDeck,
  emptyUnlockedPileCount,
  canActivateStat,
  isCardLegalForPile,
  isPileOpen,
  liveBoardBadges,
  livePileCard,
  neighborActivateBonuses,
  nonemptyPileCount,
  opponentOf,
  rarityHigherBy,
  stateConditionHolds,
  pileCard,
  prestigeIsShielded,
  prestigeToWin,
  revealIsBlocked,
  shouldActivateOnReveal,
  opponentSwapPilesFor,
  swapPilesFor,
  totalStats,
} from 'features/battle/rules';
import { hasTurnAction } from 'features/battle/queries';
import { tutorialOpeningPlayer, type BattleTutorial } from 'features/battle/tutorial';
import {
  BADGE_STAT_MAX,
  BATTLE_ACTIONS_PER_TURN,
  BATTLE_OPENING_ACTIONS,
  BATTLE_DECK_SIZE,
  BATTLE_MAX_HAND,
  BATTLE_MAX_ROUND,
  BATTLE_PILE_COUNT,
  BATTLE_SECOND_STARTING_HAND,
  BATTLE_STARTING_HAND,
  PILE_MAX_RARITY,
  type BattleChoice,
  type BattleCondition,
  type BattleContinuation,
  type BattleEffectTrigger,
  type BattleFx,
  type BattleFxView,
  type BattleInput,
  type BattleLogEntry,
  type BattleOp,
  type BattlePlayer,
  type BattleState,
  type BattleTarget,
  type BounceDest,
  type CardFilter,
  type PileIndex,
  type PlayerId,
  type StatDelta,
} from 'features/battle/types';
import { nextUnit, shuffleInPlace } from './rng';

const clonePiles = (piles: BattlePlayer['piles']): BattlePlayer['piles'] => [piles[0], piles[1], piles[2], piles[3]];

const zeroStats = (): BadgeStats => ({ ...ZERO_BADGE_STATS });

const clonePlayer = (player: BattlePlayer): BattlePlayer => ({
  name: player.name,
  deck: [...player.deck],
  hand: [...player.hand],
  piles: clonePiles(player.piles),
  discard: [...player.discard],
  prestige: player.prestige,
  skipActions: player.skipActions,
  extraActions: player.extraActions ?? 0,
  ...(player.isPure ? { isPure: true } : {}),
  statBoost: { ...(player.statBoost ?? zeroStats()) },
  statBoostCardId: player.statBoostCardId ?? null,
  statBoostTurnsLeft: player.statBoostTurnsLeft ?? 0,
  revealedThisTurn: [...(player.revealedThisTurn ?? [])],
  playedThisTurn: [...(player.playedThisTurn ?? [])],
  actionsTaken: player.actionsTaken ?? 0,
  badgeShield: player.badgeShield ?? false,
  prestigeShield: player.prestigeShield ?? false,
});

const cloneChoice = (choice: BattleChoice): BattleChoice => JSON.parse(JSON.stringify(choice)) as BattleChoice;

const cloneOps = (ops: BattleOp[]): BattleOp[] => JSON.parse(JSON.stringify(ops)) as BattleOp[];

const cloneContinuation = (ctx: BattleContinuation): BattleContinuation => ({
  ...ctx,
  remainingOps: cloneOps(ctx.remainingOps),
  lastDrawn: [...ctx.lastDrawn],
  lastDrawnOther: [...ctx.lastDrawnOther],
  lastRevealed: [...ctx.lastRevealed],
  lastDiscarded: [ctx.lastDiscarded[0], ctx.lastDiscarded[1]],
  discardedCount: [ctx.discardedCount?.[0] ?? 0, ctx.discardedCount?.[1] ?? 0],
  playedThisChain: [...ctx.playedThisChain],
  revealQueue: [...ctx.revealQueue],
  neighborBonuses: ctx.neighborBonuses?.map((bonus) => ({ ...bonus })),
});

/**
 * Older saved states kept stat gains on each player, keyed by badge id. Those ids are the cards themselves
 * (no `cards` table), so they fold into the shared table as they are.
 */
const cloneStatGain = (state: BattleState): Record<string, StatDelta> => {
  const merged = { ...state.players[0].statGain, ...state.players[1].statGain, ...state.statGain };
  return Object.fromEntries(Object.entries(merged).map(([id, stats]) => [id, { ...zeroStats(), ...stats }]));
};

const setCardStatGain = (state: BattleState, cardId: string, stats: StatDelta): void => {
  state.statGain = { ...(state.statGain ?? {}), [cardId]: stats };
};

const cloneState = (state: BattleState): BattleState => ({
  ...state,
  cards: { ...state.cards },
  statGain: cloneStatGain(state),
  onRevealFired: state.onRevealFired ? { ...state.onRevealFired } : undefined,
  players: [clonePlayer(state.players[0]), clonePlayer(state.players[1])],
  pending: state.pending ? cloneChoice(state.pending) : null,
  stack: state.stack.map(cloneContinuation),
  log: state.log.map((entry) => ({
    ...entry,
    details: entry.details ? [...entry.details] : undefined,
    causedBy: entry.causedBy?.map((item) => ({ ...item })),
  })),
  fx: [...(state.fx ?? [])],
});

const pileLabel = (pile: PileIndex): string => RARITY_TIER_LABELS[PILE_MAX_RARITY[pile]];

const pushLog = (state: BattleState, entry: BattleLogEntry): void => {
  if (entry.kind !== 'play' && state.stack.length > 0) {
    let index = -1;
    for (let i = state.log.length - 1; i >= 0; i--) {
      if (state.log[i].kind === 'play') {
        index = i;
        break;
      }
    }
    if (index >= 0) {
      const play = state.log[index];
      state.log = state.log
        .map((item, i) => (i === index ? { ...play, details: [...(play.details ?? []), entry.text] } : item))
        .slice(-48);
      return;
    }
  }
  state.log = [...state.log, entry].slice(-48);
};

const log = (state: BattleState, message: string, kind: BattleLogEntry['kind'] = 'info'): void => {
  pushLog(state, { kind, text: message });
};

const logDraw = (
  state: BattleState,
  who: PlayerId,
  drawn: readonly string[],
  kind: BattleLogEntry['kind'] = 'info',
  source?: PlayerId
): void => {
  const name = state.players[who].name;
  const from = source != null && source !== who ? ` from ${state.players[source].name}'s deck` : '';
  if (!drawn.length) {
    pushLog(state, { kind, player: who, text: `${name} drew nothing${from}.` });
    return;
  }
  const hideNames = state.cpu == null || state.cpu === who;
  pushLog(state, {
    kind,
    player: who,
    text: hideNames
      ? `${name} drew ${drawn.length} badge${drawn.length === 1 ? '' : 's'}${from}.`
      : `${name} drew ${drawn.map((cardId) => cardName(state, cardId)).join(', ')}${from}.`,
  });
};

const logPlay = (
  state: BattleState,
  player: PlayerId,
  cardId: string,
  effect: { stat: BadgeStatKey; text: string; trigger: BattleEffectTrigger },
  causedBy?: BattleLogEntry['causedBy']
): void => {
  pushLog(state, {
    kind: 'play',
    player,
    cardId,
    stat: effect.stat,
    trigger: effect.trigger,
    effectText: effect.text,
    text: `${state.players[player].name} activated ${cardName(state, cardId)}.`,
    causedBy: causedBy?.length ? causedBy.map((item) => ({ ...item })) : undefined,
  });
};

const endBattle = (state: BattleState): void => {
  const a = state.players[0].prestige;
  const b = state.players[1].prestige;
  state.winner = a === b ? 'tie' : a > b ? 0 : 1;
  state.phase = 'game_over';
  state.pending = null;
  state.stack = [];
  log(state, state.winner === 'tie' ? 'The battle is a draw.' : `${state.players[state.winner].name} wins!`);
};

/** Ends the battle as a loss for `loser`, even mid-turn. */
const applyForfeit = (state: BattleState, loser: PlayerId): void => {
  const winner = opponentOf(loser);
  state.winner = winner;
  state.phase = 'game_over';
  state.pending = null;
  state.stack = [];
  state.undo = null;
  state.concededBy = loser;
  log(state, `${state.players[loser].name} conceded. ${state.players[winner].name} wins!`);
};

export const forfeitBattle = (state: BattleState, loser: PlayerId): BattleState => {
  if (state.phase === 'game_over') return state;
  const next = cloneState(state);
  next.fx = [];
  applyForfeit(next, loser);
  return next;
};

const maybeAutoConcede = (state: BattleState): void => {
  const loser = autoConcedeLoser(state);
  if (loser == null) return;
  applyForfeit(state, loser);
};

const prestigeLogIncludes = (state: BattleState, text: string): boolean =>
  state.log.some((entry) => entry.text.includes(text) || (entry.details ?? []).some((line) => line.includes(text)));

const announcePrestigeReached = (state: BattleState, leader: PlayerId): void => {
  const text = `${state.players[leader].name} reached ${prestigeToWin(state)} Prestige.`;
  if (!prestigeLogIncludes(state, text)) log(state, text);
};

/**
 * First to the Prestige target wins on the spot. A tie at that total is a draw.
 * Let the activation that scored finish its remaining ops; pumpStack re-checks once the stack empties.
 */
const maybeEndByPrestige = (state: BattleState, when: 'gain' | 'turn_end' = 'gain'): boolean => {
  if (state.phase === 'game_over') return true;
  const needed = prestigeToWin(state);
  const a = state.players[0].prestige;
  const b = state.players[1].prestige;
  if (a < needed && b < needed) return false;
  if (when === 'gain' && state.stack.length > 0) return false;
  if (a !== b) announcePrestigeReached(state, a > b ? 0 : 1);
  endBattle(state);
  return true;
};

const matchesFilter = (state: BattleState, cardId: string, filter: CardFilter): boolean => {
  if (filter.kind === 'any') return true;
  if (filter.kind === 'rarity') return cardRarity(state, cardId) === filter.rarity;
  if (filter.kind === 'not_rarity') return cardRarity(state, cardId) !== filter.rarity;
  if (filter.kind === 'min_rarity')
    return rarityOrderIndex(cardRarity(state, cardId)) <= rarityOrderIndex(filter.rarity);
  const stats = cardTotalStats(state, cardId);
  if (filter.kind === 'stat') return stats[filter.stat] > 0;
  if (filter.kind === 'any_stat') return filter.stats.some((stat) => stats[stat] > 0);
  return filter.stats.every((stat) => stats[stat] > 0);
};

const isRarityAtLeast = (got: RarityName, min: RarityName): boolean => rarityOrderIndex(got) <= rarityOrderIndex(min);

const shareAStat = (state: BattleState, a: string, b: string): boolean =>
  BADGE_STAT_KEYS.some((key) => cardTotalStats(state, a)[key] > 0 && cardTotalStats(state, b)[key] > 0);

const emptyPlayer = (name: string, deck: string[], isPure?: boolean): BattlePlayer => ({
  name,
  deck,
  hand: [],
  piles: [null, null, null, null],
  discard: [],
  prestige: 0,
  skipActions: 0,
  extraActions: 0,
  ...(isPure ? { isPure: true } : {}),
  statBoost: zeroStats(),
  statBoostCardId: null,
  statBoostTurnsLeft: 0,
  revealedThisTurn: [],
  playedThisTurn: [],
  actionsTaken: 0,
  badgeShield: false,
  prestigeShield: false,
});

const pushFx = (
  state: BattleState,
  kind: BattleFx['kind'],
  player: PlayerId,
  cardId: string,
  extra?: Partial<Pick<BattleFx, 'to' | 'pile' | 'stat' | 'amount' | 'replaced' | 'picked'>>
): void => {
  const view: BattleFxView = {
    players: [clonePlayer(state.players[0]), clonePlayer(state.players[1])],
    cards: state.cards ? { ...state.cards } : undefined,
    statGain: state.statGain ? { ...state.statGain } : undefined,
    removedLeftPiles: state.removedLeftPiles,
    pileCount: state.pileCount,
  };
  state.fx = [...state.fx, { kind, player, cardId, ...extra, view }];
};

/** Changes a player's Prestige and shows the change. */
const addPrestige = (state: BattleState, player: PlayerId, amount: number): void => {
  if (!amount) return;
  state.players[player].prestige += amount;
  pushFx(state, 'prestige', player, '', { amount });
};

const receiveInHand = (state: BattleState, player: PlayerId, cardId: string): boolean => {
  const who = state.players[player];
  if (who.hand.length < BATTLE_MAX_HAND) {
    who.hand.push(cardId);
    return true;
  }
  who.discard.push(cardId);
  pushFx(state, 'discard', player, cardId);
  log(state, `${who.name} discarded ${cardName(state, cardId)} (hand full).`);
  return false;
};

const drawFromDeck = (
  state: BattleState,
  player: PlayerId,
  count: number,
  showFx = true,
  into: PlayerId = player
): string[] => {
  const drawn: string[] = [];
  const who = state.players[player];
  for (let i = 0; i < count; i++) {
    const card = who.deck.shift();
    if (!card) break;
    if (receiveInHand(state, into, card) && showFx) pushFx(state, 'draw', into, card);
    drawn.push(card);
  }
  return drawn;
};

const takeFromHand = (player: BattlePlayer, cardId: string): boolean => {
  const index = player.hand.indexOf(cardId);
  if (index < 0) return false;
  player.hand.splice(index, 1);
  return true;
};

const putOnPile = (player: BattlePlayer, cardId: string, pile: PileIndex): void => {
  player.piles[pile] = cardId;
};

const takeFromPile = (player: BattlePlayer, pile: PileIndex): string | null => {
  const cardId = player.piles[pile];
  player.piles[pile] = null;
  return cardId;
};

const pickRandom = <T>(state: BattleState, items: readonly T[]): T | null => {
  if (!items.length) return null;
  const roll = nextUnit(state.rng);
  state.rng = roll.rng;
  return items[Math.floor(roll.value * items.length)] ?? null;
};

const randomHandCard = (state: BattleState, player: PlayerId): string | null =>
  pickRandom(state, state.players[player].hand);

const neighborPile = (sourcePile: PileIndex | null, side: 'left' | 'right', state?: BattleState): PileIndex | null => {
  if (sourcePile == null) return null;
  const neighbor = sourcePile + (side === 'left' ? -1 : 1);
  if (neighbor < 0 || neighbor > 3) return null;
  if (state && (neighbor < battleFirstPile(state) || neighbor >= battlePileCount(state))) return null;
  return neighbor as PileIndex;
};

const opponentShields = (state: BattleState, owner: PlayerId): boolean => {
  if (!state.players[owner].badgeShield) return false;
  const ctx = state.stack[state.stack.length - 1];
  return !!ctx && ctx.actor !== owner;
};

const discardCard = (state: BattleState, player: PlayerId, cardId: string, reason: string): void => {
  if (opponentShields(state, player)) {
    log(state, `${state.players[player].name}'s ${cardName(state, cardId)} is protected.`);
    return;
  }
  if (!takeFromHand(state.players[player], cardId)) return;
  state.players[player].discard.push(cardId);
  const ctx = state.stack[state.stack.length - 1];
  if (ctx) {
    ctx.lastDiscarded[player] = cardId;
    ctx.discardedCount[player] += 1;
  }
  pushFx(state, 'discard', player, cardId);
  log(state, `${state.players[player].name} discarded ${cardName(state, cardId)}${reason}.`);
};

const newContinuation = (
  actor: PlayerId,
  sourceCardId: string,
  sourcePile: PileIndex | null,
  ops: BattleOp[],
  alreadyPlayed: string[] = [],
  consumedAction = false
): BattleContinuation => ({
  actor,
  sourceCardId,
  sourcePile,
  remainingOps: cloneOps(ops),
  lastDrawn: [],
  lastDrawnOther: [],
  lastRevealed: [],
  lastDiscarded: [null, null],
  discardedCount: [0, 0],
  playOtherLeft: 0,
  playedThisChain: [...alreadyPlayed, sourceCardId],
  returnedOwnBadge: false,
  activationMissed: false,
  statGainReached: null,
  consumedAction,
  revealQueue: [],
  revealPlayer: actor,
  playAfterReveal: false,
});

const lastDrawnCard = (ctx: BattleContinuation): string | null => ctx.lastDrawn.at(-1) ?? null;

const discardedRarityRank = (state: BattleState, cardId: string | null): number =>
  cardId ? rarityOrderIndex(cardRarity(state, cardId)) : Number.POSITIVE_INFINITY;

const representedRarities = (state: BattleState, player: BattlePlayer): Set<string> =>
  new Set(liveBoardBadges(state, player).map(({ cardId }) => cardRarity(state, cardId)));

const matchesCondition = (state: BattleState, ctx: BattleContinuation, condition: BattleCondition): boolean => {
  const actor = ctx.actor;
  const foe = opponentOf(actor);
  const self = state.players[actor];
  const other = state.players[foe];

  const shared = stateConditionHolds(state, actor, condition, ctx.sourcePile, matchesFilter);
  if (shared !== undefined) return shared;
  if (condition.kind === 'neither_deck_empty') return self.deck.length > 0 && other.deck.length > 0;
  if (condition.kind === 'effect_revealed') return ctx.lastRevealed.length > 0;
  if (condition.kind === 'drawn_rarities_match') {
    const drawn = ctx.lastDrawn[0];
    const otherDrawn = ctx.lastDrawnOther[0];
    return !!drawn && !!otherDrawn && cardRarity(state, drawn) === cardRarity(state, otherDrawn);
  }
  if (condition.kind === 'opponent_board_badge') return bounceablePiles(state, foe, condition.filter).length > 0;
  if (condition.kind === 'discarded_rarities_match') {
    const a = ctx.lastDiscarded[actor];
    const b = ctx.lastDiscarded[foe];
    return !!a && !!b && cardRarity(state, a) === cardRarity(state, b);
  }
  if (condition.kind === 'discarded_self_rarity') {
    const cardId = ctx.lastDiscarded[actor];
    return !!cardId && cardRarity(state, cardId) === condition.rarity;
  }
  if (condition.kind === 'discarded_rarity') {
    const who = condition.who === 'self' ? actor : foe;
    const cardId = ctx.lastDiscarded[who];
    return !!cardId && cardRarity(state, cardId) === condition.rarity;
  }
  if (condition.kind === 'discarded_self_higher' || condition.kind === 'discarded_opponent_higher') {
    const a = discardedRarityRank(state, ctx.lastDiscarded[actor]);
    const b = discardedRarityRank(state, ctx.lastDiscarded[foe]);
    if (a === b) return false;
    return condition.kind === 'discarded_self_higher' ? a < b : b < a;
  }
  if (condition.kind === 'discarded_at_least') {
    const count =
      condition.who === 'all'
        ? ctx.discardedCount[actor] + ctx.discardedCount[foe]
        : ctx.discardedCount[condition.who === 'opponent' ? foe : actor];
    return count >= condition.count;
  }
  if (condition.kind === 'discarded_rarity_at_least') {
    const who = condition.who === 'self' ? actor : foe;
    const cardId = ctx.lastDiscarded[who];
    return !!cardId && isRarityAtLeast(cardRarity(state, cardId), condition.rarity);
  }
  if (condition.kind === 'other_action_this_turn') {
    const revealed = self.revealedThisTurn ?? [];
    const played = self.playedThisTurn ?? [];
    return revealed.some((id) => id !== ctx.sourceCardId) || played.some((id) => id !== ctx.sourceCardId);
  }
  if (condition.kind === 'first_action') return ctx.consumedAction && self.actionsTaken === 1;
  if (condition.kind === 'second_action') return ctx.consumedAction && self.actionsTaken === 2;
  if (condition.kind === 'badge_revealed_or_played_this_turn') {
    const revealed = self.revealedThisTurn ?? [];
    const played = self.playedThisTurn ?? [];
    return [...revealed, ...played].some(
      (cardId) =>
        (!condition.excludeSource || cardId !== ctx.sourceCardId) && matchesFilter(state, cardId, condition.filter)
    );
  }
  if (condition.kind === 'effect_played_this_turn') {
    return (self.playedThisTurn ?? []).some(
      (cardId) =>
        cardId !== ctx.sourceCardId && cardEffects(state, cardId).some((effect) => effect.stat === condition.stat)
    );
  }
  if (condition.kind === 'returned_own_badge') return ctx.returnedOwnBadge;
  if (condition.kind === 'activation_missed') return ctx.activationMissed;
  if (condition.kind === 'stat_gain_reaches') {
    const hit = ctx.statGainReached;
    return !!hit && hit.stat === condition.stat && hit.total === condition.total;
  }
  if (condition.kind === 'source_stat_zero') {
    const gained = cardStatGain(state, ctx.sourceCardId)?.[condition.stat] ?? 0;
    return cardStats(state, ctx.sourceCardId)[condition.stat] + gained <= 0;
  }
  const drawn = lastDrawnCard(ctx);
  if (!drawn) return false;
  const rarity = cardRarity(state, drawn);
  if (condition.kind === 'drawn_rarity') return rarity === condition.rarity;
  if (condition.kind === 'drawn_matches_source_rarity') return rarity === cardRarity(state, ctx.sourceCardId);
  if (condition.kind === 'drawn_matches_filter') return matchesFilter(state, drawn, condition.filter);
  if (condition.kind === 'only_self_drawn_matches_filter') {
    if (!matchesFilter(state, drawn, condition.filter)) return false;
    return !ctx.lastDrawnOther.some((cardId) => matchesFilter(state, cardId, condition.filter));
  }
  if (condition.kind === 'drawn_rarity_unrepresented') return !representedRarities(state, self).has(rarity);
  if (condition.kind === 'drawn_rarity_represented') return representedRarities(state, self).has(rarity);
  if (condition.kind === 'drawn_rarity_matches_opponent_board_badge')
    return representedRarities(state, other).has(rarity);
  if (condition.kind === 'last_two_drawn_same_rarity') {
    if (ctx.lastDrawn.length < 2) return false;
    return (
      cardRarity(state, ctx.lastDrawn[ctx.lastDrawn.length - 1]) ===
      cardRarity(state, ctx.lastDrawn[ctx.lastDrawn.length - 2])
    );
  }
  if (condition.kind === 'last_two_drawn_share_stat') {
    if (ctx.lastDrawn.length < 2) return false;
    return shareAStat(state, ctx.lastDrawn[ctx.lastDrawn.length - 1], ctx.lastDrawn[ctx.lastDrawn.length - 2]);
  }
  if (condition.kind === 'last_two_drawn_match_filter') {
    if (ctx.lastDrawn.length < 2) return false;
    const a = ctx.lastDrawn[ctx.lastDrawn.length - 1];
    const b = ctx.lastDrawn[ctx.lastDrawn.length - 2];
    return matchesFilter(state, a, condition.filter) && matchesFilter(state, b, condition.filter);
  }
  return false;
};

const bounceablePiles = (
  state: BattleState,
  owner: PlayerId,
  filter?: CardFilter,
  excludeCardId?: string
): PileIndex[] =>
  liveBoardBadges(state, state.players[owner])
    .filter(({ cardId }) => cardId !== excludeCardId && (!filter || matchesFilter(state, cardId, filter)))
    .map(({ pile }) => pile);

const returnToDeck = (
  state: BattleState,
  player: PlayerId,
  cardId: string,
  spot: 'top' | 'bottom' | 'random'
): void => {
  const deck = state.players[player].deck;
  if (spot === 'top') {
    deck.unshift(cardId);
    return;
  }
  if (spot === 'bottom') {
    deck.push(cardId);
    return;
  }
  const roll = nextUnit(state.rng);
  state.rng = roll.rng;
  const index = Math.floor(roll.value * (deck.length + 1));
  deck.splice(index, 0, cardId);
};

const returnToDeckBottom = (state: BattleState, player: PlayerId, cardId: string): void => {
  returnToDeck(state, player, cardId, 'bottom');
};

const bottomDrawnCard = (state: BattleState, player: PlayerId, cardId: string): void => {
  if (!takeFromHand(state.players[player], cardId)) return;
  returnToDeckBottom(state, player, cardId);
  pushFx(state, 'return', player, cardId, { to: 'deck' });
  log(state, `${state.players[player].name} put a badge on the bottom of their deck.`);
};

const displacePile = (
  state: BattleState,
  player: PlayerId,
  pile: PileIndex,
  to: BounceDest = 'hand',
  showFx = true,
  receiver?: PlayerId,
  picked?: true
): void => {
  if (opponentShields(state, player)) {
    const protectedId = pileCard(state.players[player], pile);
    if (protectedId) log(state, `${state.players[player].name}'s ${cardName(state, protectedId)} is protected.`);
    return;
  }
  const cardId = takeFromPile(state.players[player], pile);
  if (!cardId) return;
  if (to === 'discard') {
    discardReplacedBadge(state, player, cardId, showFx, { pile, picked });
    return;
  }
  const dest = receiver ?? player;
  if (to === 'deck' || to === 'top' || to === 'bottom') {
    returnToDeck(state, dest, cardId, to === 'deck' ? 'random' : to);
    if (showFx) pushFx(state, 'return', dest, cardId, { to: 'deck', pile, picked });
    log(
      state,
      to === 'deck'
        ? `${cardName(state, cardId)} returned to a random spot in ${state.players[dest].name}'s deck.`
        : `${cardName(state, cardId)} returned to the ${to} of ${state.players[dest].name}'s deck.`
    );
    return;
  }
  if (receiveInHand(state, dest, cardId)) {
    if (showFx) pushFx(state, 'return', dest, cardId, { to: 'hand', pile, picked });
    log(state, `${cardName(state, cardId)} returned to ${state.players[dest].name}'s hand.`);
  }
};

/** `origin.pile`: where an effect took the badge from, so the view can mark it as targeted first. */
const discardReplacedBadge = (
  state: BattleState,
  player: PlayerId,
  cardId: string,
  showFx: boolean,
  origin?: { pile?: PileIndex; replaced?: true; picked?: true }
): void => {
  state.players[player].discard.push(cardId);
  if (showFx) pushFx(state, 'discard', player, cardId, origin);
  log(state, `${state.players[player].name} discarded ${cardName(state, cardId)}.`);
};

const discardOwnBoardBadge = (state: BattleState, player: PlayerId, pile: PileIndex, picked?: true): void => {
  const cardId = takeFromPile(state.players[player], pile);
  if (!cardId) return;
  discardReplacedBadge(state, player, cardId, true, { pile, picked });
  const ctx = state.stack[state.stack.length - 1];
  if (ctx) {
    ctx.lastDiscarded[player] = cardId;
    ctx.discardedCount[player] += 1;
  }
};

const takeFromDeck = (player: BattlePlayer, cardId: string): boolean => {
  const index = player.deck.indexOf(cardId);
  if (index < 0) return false;
  player.deck.splice(index, 1);
  return true;
};

/** A fresh card instance of `badgeId`. Its id never collides with a badge id or another card. */
const spawnBadge = (state: BattleState, badgeId: string): string => {
  const cards = (state.cards ??= {});
  let next = Object.keys(cards).length;
  while (cards[`#${next}`] != null) next++;
  const cardId = `#${next}`;
  cards[cardId] = badgeId;
  return cardId;
};

const replaceOwnBadge = (state: BattleState, player: PlayerId, pile: PileIndex, incoming: string): string | null => {
  if (revealIsBlocked(state, incoming)) return null;
  const replaced = takeFromPile(state.players[player], pile);
  if (!replaced) return null;
  putOnPile(state.players[player], incoming, pile);
  pushFx(state, 'reveal', player, incoming, { pile });
  log(
    state,
    `${state.players[player].name} revealed ${cardName(state, incoming)} onto the ${pileLabel(pile)} pile.`,
    'reveal'
  );
  startOnReveal(state, player, pile);
  maybeActivateOnReveal(state, player, pile);
  return replaced;
};

/** One badge from the whole deck, so the named rarity follows the deck mix and not the top badge. */
const guessRarityFromDeck = (state: BattleState, cardIds: readonly string[]): RarityName | null => {
  const cardId = pickRandom(state, cardIds);
  return cardId ? cardRarity(state, cardId) : null;
};

const guessRarityFromHand = (state: BattleState, hand: readonly string[]): RarityName => {
  const counts = new Map<RarityName, number>();
  hand.forEach((cardId) => {
    const rarity = cardRarity(state, cardId);
    counts.set(rarity, (counts.get(rarity) ?? 0) + 1);
  });
  return [...counts.entries()].sort((a, b) => b[1] - a[1] || rarityOrderIndex(a[0]) - rarityOrderIndex(b[0]))[0][0];
};

const resolveNamedRarityDiscard = (state: BattleState, actor: PlayerId, rarity: RarityName): void => {
  const foe = opponentOf(actor);
  log(state, `${state.players[actor].name} named ${RARITY_TIER_LABELS[rarity]}.`);
  const hits = state.players[foe].hand.filter((cardId) => cardRarity(state, cardId) === rarity);
  hits.forEach((cardId) => discardCard(state, foe, cardId, ''));
};

const bestScryCard = (state: BattleState, cardIds: readonly string[]): string | null => {
  if (!cardIds.length) return null;
  const statTotal = (cardId: string): number =>
    BADGE_STAT_KEYS.reduce((sum, key) => sum + cardStats(state, cardId)[key], 0);
  return [...cardIds].sort(
    (a, b) =>
      rarityOrderIndex(cardRarity(state, a)) - rarityOrderIndex(cardRarity(state, b)) || statTotal(b) - statTotal(a)
  )[0];
};

const drawPeekedCard = (state: BattleState, player: PlayerId, peeked: readonly string[], chosen: string): boolean => {
  const deck = state.players[player].deck;
  if (!peeked.includes(chosen) || deck.length < peeked.length) return false;
  for (let i = 0; i < peeked.length; i++) {
    if (deck[i] !== peeked[i]) return false;
  }
  deck.splice(0, peeked.length);
  deck.push(...peeked.filter((cardId) => cardId !== chosen));
  if (receiveInHand(state, player, chosen)) pushFx(state, 'draw', player, chosen);
  logDraw(state, player, [chosen]);
  return true;
};

const tryRevealCard = (
  state: BattleState,
  player: PlayerId,
  cardId: string,
  pile: PileIndex,
  fromHand = true
): boolean => {
  const legal = cardLegalPiles(state, cardId, state.round);
  if (!legal.includes(pile)) return false;
  if (fromHand && !takeFromHand(state.players[player], cardId)) return false;
  const replaced = takeFromPile(state.players[player], pile);
  putOnPile(state.players[player], cardId, pile);
  pushFx(state, 'reveal', player, cardId, { pile });
  if (replaced) discardReplacedBadge(state, player, replaced, true, { replaced: true });
  log(
    state,
    `${state.players[player].name} revealed ${cardName(state, cardId)} onto the ${pileLabel(pile)} pile.`,
    'reveal'
  );
  state.players[player].revealedThisTurn.push(cardId);
  const ctx = state.stack[state.stack.length - 1];
  if (ctx) ctx.lastRevealed.push(cardId);
  startOnReveal(state, player, pile);
  maybeActivateOnReveal(state, player, pile);
  return true;
};

/** Piles the effect's source badge can swap with. None once the source has left its pile. */
const movePilesFor = (state: BattleState, ctx: BattleContinuation): PileIndex[] => {
  const from = ctx.sourcePile;
  if (from == null || pileCard(state.players[ctx.actor], from) !== ctx.sourceCardId) return [];
  return swapPilesFor(state, ctx.actor, from);
};

/** Swaps the source badge with the badge on `pile`. False if no swap happened. */
const moveSourceBadge = (state: BattleState, ctx: BattleContinuation, pile: PileIndex): boolean => {
  if (!movePilesFor(state, ctx).includes(pile)) return false;
  const player = state.players[ctx.actor];
  const from = ctx.sourcePile as PileIndex;
  const occupant = takeFromPile(player, pile) as string;
  takeFromPile(player, from);
  putOnPile(player, ctx.sourceCardId, pile);
  putOnPile(player, occupant, from);
  pushFx(state, 'swap', ctx.actor, ctx.sourceCardId, { pile });
  pushFx(state, 'swap', ctx.actor, occupant, { pile: from });
  ctx.sourcePile = pile;
  log(state, `${player.name} swapped ${cardName(state, ctx.sourceCardId)} with ${cardName(state, occupant)}.`);
  return true;
};

/** Opponent piles the source badge can swap with. None once the source has left its pile. */
const opponentSwapPilesForSource = (state: BattleState, ctx: BattleContinuation): PileIndex[] => {
  const from = ctx.sourcePile;
  if (from == null || pileCard(state.players[ctx.actor], from) !== ctx.sourceCardId) return [];
  return opponentSwapPilesFor(state, ctx.actor, from);
};

/** Swaps the source badge with the opponent's badge on `pile`. False if no swap happened. */
const swapSourceWithOpponent = (state: BattleState, ctx: BattleContinuation, pile: PileIndex): boolean => {
  if (!opponentSwapPilesForSource(state, ctx).includes(pile)) return false;
  const player = state.players[ctx.actor];
  const foe = state.players[opponentOf(ctx.actor)];
  const from = ctx.sourcePile as PileIndex;
  const occupant = takeFromPile(foe, pile) as string;
  takeFromPile(player, from);
  putOnPile(foe, ctx.sourceCardId, pile);
  putOnPile(player, occupant, from);
  pushFx(state, 'swap', opponentOf(ctx.actor), ctx.sourceCardId, { pile });
  pushFx(state, 'swap', ctx.actor, occupant, { pile: from });
  log(
    state,
    `${player.name} swapped ${cardName(state, ctx.sourceCardId)} with ${foe.name}'s ${cardName(state, occupant)}.`
  );
  return true;
};

const startPlayingCard = (
  state: BattleState,
  player: PlayerId,
  pile: PileIndex,
  consumeAction: boolean,
  trigger: BattleEffectTrigger = 'Activate'
): boolean => {
  const cardId = pileCard(state.players[player], pile);
  if (!cardId) return false;
  if (trigger === 'Activate' && state.stack.some((frame) => frame.sourceCardId === cardId)) return true;
  const effect = cardEffects(state, cardId)[0];
  if (!effect?.stat || effect.trigger !== trigger) return false;
  if (!canActivateStat(state, player, effect.stat)) return false;
  if (effectRequiresDeck(effect.ops) && state.players[player].deck.length === 0) return false;
  const parent = state.stack[state.stack.length - 1];
  const alreadyPlayed = parent?.playedThisChain ?? [];
  // Nested Activate still happens on the action that revealed it; it does not spend another action.
  const inheritedAction = trigger === 'Activate' && state.stack.some((frame) => frame.consumedAction);
  const bonuses = trigger === 'Activate' ? neighborActivateBonuses(state, player, pile) : [];
  if (effectDoesNothing(state, player, pile, effect.ops, consumeAction)) return false;
  if (consumeAction) {
    state.actionsLeft -= 1;
    state.players[player].actionsTaken += 1;
  }
  state.players[player].playedThisTurn.push(cardId);
  logPlay(
    state,
    player,
    cardId,
    { stat: effect.stat, text: effect.text, trigger: effect.trigger },
    bonuses.map((bonus) => ({ cardId: bonus.cardId, stat: bonus.stat, trigger: 'Ongoing', text: bonus.text }))
  );
  pushFx(state, 'play', player, cardId, { pile });
  const ctx = newContinuation(player, cardId, pile, effect.ops, alreadyPlayed, consumeAction || inheritedAction);
  if (bonuses.length) ctx.neighborBonuses = bonuses.map((bonus) => ({ cardId: bonus.cardId, amount: bonus.amount }));
  state.stack.push(ctx);
  return true;
};

const recordOnReveal = (state: BattleState, cardId: string, fired: boolean): void => {
  state.onRevealFired = { ...state.onRevealFired, [cardId]: fired };
};

const startOnReveal = (state: BattleState, player: PlayerId, pile: PileIndex): boolean => {
  const cardId = pileCard(state.players[player], pile);
  const effect = cardId ? cardEffects(state, cardId)[0] : undefined;
  if (!cardId || effect?.trigger !== 'On Reveal') return false;
  const fired = startPlayingCard(state, player, pile, false, 'On Reveal');
  recordOnReveal(state, cardId, fired);
  return fired;
};

/** Leading Ongoing `activate_on_reveal`: your matching badges Activate once on this reveal, if able. */
const maybeActivateOnReveal = (state: BattleState, player: PlayerId, pile: PileIndex): void => {
  const cardId = pileCard(state.players[player], pile);
  if (!cardId || !shouldActivateOnReveal(state, player, cardId)) return;
  startPlayingCard(state, player, pile, false);
};

const queueRevealFromCards = (
  state: BattleState,
  ctx: BattleContinuation,
  owner: PlayerId,
  cardIds: readonly string[],
  filter: CardFilter,
  play: boolean,
  first = false
): void => {
  ctx.playAfterReveal = play;
  ctx.revealPlayer = owner;
  const matches = cardIds.filter(
    (cardId) =>
      state.players[owner].hand.includes(cardId) &&
      matchesFilter(state, cardId, filter) &&
      cardLegalPiles(state, cardId, state.round).length > 0
  );
  if (!first) {
    ctx.revealQueue = matches;
    return;
  }
  if (!matches.length) {
    ctx.revealQueue = [];
    return;
  }
  // One legal badge is the only choice, so select it and leave the pile pick to the player.
  if (matches.length === 1) {
    ctx.revealQueue = [matches[0]];
    return;
  }
  ctx.revealQueue = [];
  state.pending = {
    type: 'choose_hand',
    player: owner,
    cardIds: matches,
    count: 1,
    purpose: 'reveal',
  };
  state.phase = 'choice';
};

const queueRevealFromDrawn = (
  state: BattleState,
  ctx: BattleContinuation,
  filter: CardFilter,
  play: boolean,
  first = false
): void => {
  queueRevealFromCards(state, ctx, ctx.actor, ctx.lastDrawn, filter, play, first);
};

const queueRevealDrawn = (state: BattleState, ctx: BattleContinuation, play: boolean): void => {
  queueRevealFromCards(state, ctx, ctx.actor, ctx.lastDrawn, { kind: 'any' }, play, true);
};

const resolveDrawnRarityGuess = (
  state: BattleState,
  actor: PlayerId,
  rarity: RarityName,
  count: number,
  reward: 'reveal' | 'prestige'
): void => {
  const ctx = state.stack[state.stack.length - 1];
  if (!ctx) return;
  const drawn = drawFromDeck(state, actor, count);
  ctx.lastDrawn = drawn;
  logDraw(state, actor, drawn);
  const name = state.players[actor].name;
  const hits = drawn.filter((cardId) => cardRarity(state, cardId) === rarity);
  log(state, `${name} named ${RARITY_TIER_LABELS[rarity]}.`);
  if (!drawn.length) return;
  if (reward === 'prestige') {
    if (!hits.length) {
      log(state, `${name} gained no Prestige.`);
      return;
    }
    addPrestige(state, actor, hits.length);
    log(state, `${name} gained ${hits.length} Prestige.`);
    maybeEndByPrestige(state);
    return;
  }
  const revealable = hits.filter(
    (cardId) => state.players[actor].hand.includes(cardId) && cardLegalPiles(state, cardId, state.round).length > 0
  );
  if (!revealable.length) {
    log(state, hits.length ? 'That badge cannot be revealed yet.' : 'The guess missed.');
    return;
  }
  queueRevealFromCards(state, ctx, actor, revealable, { kind: 'rarity', rarity }, false, false);
  processRevealQueue(state, ctx);
};

const processRevealQueue = (state: BattleState, ctx: BattleContinuation): void => {
  const owner = ctx.revealPlayer;
  while (ctx.revealQueue.length && !state.pending) {
    const cardId = ctx.revealQueue[0];
    const piles = cardLegalPiles(state, cardId, state.round);
    if (!piles.length || !state.players[owner].hand.includes(cardId)) {
      ctx.revealQueue.shift();
      continue;
    }
    if (piles.length === 1 && !actorPicks(state, owner)) {
      ctx.revealQueue.shift();
      const stackBefore = state.stack.length;
      const placed = tryRevealCard(state, owner, cardId, piles[0]);
      if (placed && ctx.playAfterReveal) startPlayingCard(state, owner, piles[0], false);
      if (state.stack.length > stackBefore) return;
      continue;
    }
    state.pending = { type: 'choose_pile', player: owner, cardId, piles, purpose: 'reveal' };
    state.phase = 'choice';
    return;
  }
};

/** Queues a hand pick for reveal, keeping the other options so the pile step can undo the pick. */
const pickRevealFromHand = (state: BattleState, ctx: BattleContinuation, cardId: string, options: string[]): void => {
  ctx.revealQueue = [cardId];
  processRevealQueue(state, ctx);
  const next = state.pending;
  if (next?.type === 'choose_pile' && next.cardId === cardId && options.length > 1) {
    state.pending = { ...next, options: [...options] };
  }
};

export const bestNextTurnBoostStat = (
  state: BattleState,
  actor: PlayerId,
  stats: readonly BadgeStatKey[],
  amount: number
): BadgeStatKey => {
  const self = totalStats(state, state.players[actor]);
  const foe = totalStats(state, state.players[opponentOf(actor)]);
  return [...stats].sort((a, b) => self[b] + amount - foe[b] - (self[a] + amount - foe[a]))[0];
};

const applyGainStat = (
  state: BattleState,
  ctx: BattleContinuation,
  stat: BadgeStatKey,
  amount: number,
  max?: number
): void => {
  const who = state.players[ctx.actor];
  const current = cardStatGain(state, ctx.sourceCardId) ?? zeroStats();
  const printed = cardStats(state, ctx.sourceCardId)[stat];
  const cap = Math.min(max ?? BADGE_STAT_MAX, BADGE_STAT_MAX);
  const room = Math.max(0, cap - printed - current[stat]);
  const added = Math.min(amount, room);
  if (!added) {
    ctx.statGainReached = null;
    log(state, `${BADGE_STAT_LABELS[stat]} is already at ${cap}.`);
    return;
  }
  const total = printed + current[stat] + added;
  setCardStatGain(state, ctx.sourceCardId, { ...current, [stat]: current[stat] + added });
  pushStatFx(state, ctx.actor, ctx.sourceCardId, stat, added);
  ctx.statGainReached = { stat, total };
  log(state, `${who.name} gained ${added} ${BADGE_STAT_LABELS[stat]}.`);
};

/** Pops the stat change over the card's pile, wherever the card sits on its owner's board. */
const pushStatFx = (state: BattleState, owner: PlayerId, cardId: string, stat: BadgeStatKey, amount: number): void => {
  const pile = state.players[owner].piles.indexOf(cardId);
  if (pile < 0) return;
  pushFx(state, 'stat', owner, cardId, { pile: pile as PileIndex, stat, amount });
};

const grantStatBoost = (
  state: BattleState,
  actor: PlayerId,
  stat: BadgeStatKey,
  amount: number,
  cardId: string
): void => {
  const who = state.players[actor];
  who.statBoost = { ...ZERO_BADGE_STATS, [stat]: amount };
  who.statBoostCardId = cardId;
  who.statBoostTurnsLeft = 2;
  pushStatFx(state, actor, cardId, stat, amount);
  log(state, `${who.name} has +${amount} ${BADGE_STAT_LABELS[stat]} until the end of their next turn.`);
};

const applyOp = (state: BattleState, ctx: BattleContinuation, op: BattleOp): void => {
  const actor = ctx.actor;
  const foe = opponentOf(actor);

  if (op.kind === 'draw') {
    if (op.from === 'opponent' && op.who !== 'all') {
      const receiver: PlayerId = op.who === 'opponent' ? foe : actor;
      const drawn = drawFromDeck(state, foe, op.count, true, receiver);
      if (receiver === actor) {
        ctx.lastDrawn = drawn;
        ctx.lastDrawnOther = [];
      } else {
        ctx.lastDrawn = [];
        ctx.lastDrawnOther = drawn;
      }
      logDraw(state, receiver, drawn, 'info', foe);
      return;
    }
    const targets: PlayerId[] = op.who === 'all' ? [actor, foe] : op.who === 'opponent' ? [foe] : [actor];
    const actorDrawn: string[] = [];
    const otherDrawn: string[] = [];
    targets.forEach((who) => {
      const drawn = drawFromDeck(state, who, op.count);
      if (who === actor) actorDrawn.push(...drawn);
      else otherDrawn.push(...drawn);
      logDraw(state, who, drawn);
    });
    ctx.lastDrawn = actorDrawn;
    ctx.lastDrawnOther = otherDrawn;
    return;
  }

  if (op.kind === 'draw_up_to_tied') {
    const gap = Math.max(0, state.players[foe].hand.length - state.players[actor].hand.length);
    const drawn = drawFromDeck(state, actor, Math.min(op.max, gap));
    ctx.lastDrawn = drawn;
    ctx.lastDrawnOther = [];
    logDraw(state, actor, drawn);
    return;
  }

  if (op.kind === 'draw_up_to') {
    const need = Math.min(op.max, Math.max(0, op.size - state.players[actor].hand.length));
    const drawn = drawFromDeck(state, actor, need);
    ctx.lastDrawn = drawn;
    ctx.lastDrawnOther = [];
    logDraw(state, actor, drawn);
    return;
  }

  if (op.kind === 'reveal_if') {
    queueRevealFromDrawn(state, ctx, op.filter, op.play, op.first);
    processRevealQueue(state, ctx);
    return;
  }

  if (op.kind === 'reveal_hand') {
    const owner = op.who === 'opponent' ? foe : actor;
    queueRevealFromCards(state, ctx, owner, state.players[owner].hand, op.filter, op.play, op.first);
    processRevealQueue(state, ctx);
    return;
  }

  if (op.kind === 'reveal_hand_neighbor') {
    const neighbor = neighborPile(ctx.sourcePile, op.side, state);
    if (neighbor == null || !isPileOpen(state, neighbor) || livePileCard(state, state.players[actor], neighbor) != null)
      return;
    const eligible = state.players[actor].hand.filter((cardId) =>
      cardLegalPiles(state, cardId, state.round).includes(neighbor)
    );
    const cardId = pickRandom(state, eligible);
    if (!cardId) return;
    const placed = tryRevealCard(state, actor, cardId, neighbor);
    if (placed && op.play) startPlayingCard(state, actor, neighbor, false);
    return;
  }

  if (op.kind === 'reveal_drawn') {
    queueRevealDrawn(state, ctx, op.play);
    processRevealQueue(state, ctx);
    return;
  }

  if (op.kind === 'if') {
    const branch = matchesCondition(state, ctx, op.condition) ? op.then : (op.otherwise ?? []);
    ctx.remainingOps.unshift(...cloneOps(branch));
    return;
  }

  if (op.kind === 'discard') {
    const targets: PlayerId[] = op.who === 'all' ? [0, 1] : op.who === 'self' ? [actor] : [foe];
    const filteredHand = (who: PlayerId): string[] =>
      state.players[who].hand.filter((cardId) => !op.filter || matchesFilter(state, cardId, op.filter));
    if (!op.random) {
      const chooser = op.who === 'opponent' ? foe : actor;
      const hand = filteredHand(chooser);
      if (!hand.length || op.count <= 0) return;
      // No real pick when the hand is no larger than the discard — same for the player and the CPU.
      if (hand.length <= op.count) {
        hand.forEach((cardId) => discardCard(state, chooser, cardId, ''));
        return;
      }
      state.pending = {
        type: 'choose_hand',
        player: chooser,
        cardIds: [...hand],
        count: op.count,
        purpose: 'discard',
      };
      state.phase = 'choice';
      return;
    }
    targets.forEach((who) => {
      for (let i = 0; i < op.count; i++) {
        const cardId = pickRandom(state, filteredHand(who));
        if (cardId) discardCard(state, who, cardId, '');
      }
    });
    return;
  }

  if (op.kind === 'give') {
    const hand = state.players[actor].hand;
    if (!hand.length || op.count <= 0) {
      log(state, `${state.players[actor].name} has no badge to give.`);
      return;
    }
    if (hand.length === 1 && !actorPicks(state, actor)) {
      giveCard(state, actor, hand[0]);
      return;
    }
    state.pending = {
      type: 'choose_hand',
      player: actor,
      cardIds: [...hand],
      count: Math.min(op.count, hand.length),
      purpose: 'give',
    };
    state.phase = 'choice';
    return;
  }

  if (op.kind === 'ai_takeover') {
    if (cpuDrives(state, actor)) return;
    state.autopilot = { ...state.autopilot, [actor]: op.difficulty };
    log(
      state,
      `${state.players[actor].name} handed the rest of the battle to ${op.difficulty === 'easy' ? 'the' : 'a'} ${cpuName(op.difficulty)}.`
    );
    return;
  }

  if (
    op.kind === 'disable_piles' ||
    op.kind === 'ignore_stat_lead' ||
    op.kind === 'neighbor_activate_prestige' ||
    op.kind === 'block_reveal' ||
    op.kind === 'activate_on_reveal'
  ) {
    return;
  }

  if (op.kind === 'remove_leftmost_pile') {
    if (!canRemoveLeftmostPile(state)) {
      log(state, 'No leftmost pile left to remove.');
      return;
    }
    const pile = battleFirstPile(state) as PileIndex;
    ([0, 1] as PlayerId[]).forEach((owner) => {
      if (pileCard(state.players[owner], pile)) discardOwnBoardBadge(state, owner, pile);
    });
    state.removedLeftPiles = pile + 1;
    pushFx(state, 'remove_pile', actor, ctx.sourceCardId, { pile });
    log(state, `${state.players[actor].name} removed the ${pileLabel(pile)} pile from the battle.`);
    return;
  }

  if (op.kind === 'skip_actions_next_turn') {
    const target = op.who === 'opponent' ? foe : actor;
    const count = Math.max(0, op.count);
    if (!count) return;
    state.players[target].skipActions += count;
    log(
      state,
      count === 1
        ? `${state.players[target].name} skips an action on their next turn.`
        : `${state.players[target].name} skips ${count} actions on their next turn.`
    );
    return;
  }

  if (op.kind === 'set_next_actions') {
    const who = state.players[actor];
    const queued = BATTLE_ACTIONS_PER_TURN + (who.extraActions ?? 0);
    if (queued >= op.count) return;
    who.extraActions = op.count - BATTLE_ACTIONS_PER_TURN;
    log(state, `${who.name} takes ${op.count} actions on their next turn.`);
    return;
  }

  if (op.kind === 'discard_drawn') {
    const stillInHand = ctx.lastDrawn.filter((cardId) => state.players[actor].hand.includes(cardId));
    const limit = Math.min(op.count ?? stillInHand.length, stillInHand.length);
    if (!stillInHand.length || limit <= 0) return;
    const choose = op.count != null && (stillInHand.length > 1 || actorPicks(state, actor));
    if (choose) {
      state.pending = {
        type: 'choose_hand',
        player: actor,
        cardIds: stillInHand,
        count: limit,
        purpose: 'discard',
      };
      state.phase = 'choice';
      return;
    }
    stillInHand.slice(0, limit).forEach((cardId) => discardCard(state, actor, cardId, ''));
    return;
  }

  if (op.kind === 'discard_deck') {
    const who = state.players[actor];
    const milled = who.deck.splice(0, op.count);
    if (!milled.length) return;
    who.discard.push(...milled);
    milled.forEach((cardId) => pushFx(state, 'discard', actor, cardId));
    log(state, `${who.name} discarded ${milled.map((cardId) => cardName(state, cardId)).join(', ')} from their deck.`);
    return;
  }

  if (op.kind === 'cycle_hand') {
    const cards = [...state.players[actor].hand];
    const n = cards.length;
    cards.forEach((cardId) => discardCard(state, actor, cardId, ''));
    const drawn = drawFromDeck(state, actor, n + op.extra);
    ctx.lastDrawn = drawn;
    logDraw(state, actor, drawn);
    return;
  }

  if (op.kind === 'gain_prestige') {
    const targets: PlayerId[] = op.who === 'all' ? [actor, foe] : op.who === 'opponent' ? [foe] : [actor];
    const counted =
      op.amount === 'opponent_empty_unlocked_piles'
        ? emptyUnlockedPileCount(state.players[foe], state.round, battlePileCount(state), battleFirstPile(state), state)
        : op.amount;
    targets.forEach((who) => {
      const amount = counted === 'nonempty_piles' ? nonemptyPileCount(state.players[who], state) : counted;
      addPrestige(state, who, amount);
      log(state, `${state.players[who].name} gained ${amount} Prestige.`);
    });
    maybeEndByPrestige(state);
    return;
  }

  if (op.kind === 'gain_prestige_per_drawn') {
    const drawn = [...ctx.lastDrawn, ...ctx.lastDrawnOther];
    const amount = drawn.filter((cardId) => matchesFilter(state, cardId, op.filter)).length;
    if (!amount) {
      log(state, `${state.players[actor].name} gained no Prestige.`);
      return;
    }
    addPrestige(state, actor, amount);
    log(state, `${state.players[actor].name} gained ${amount} Prestige.`);
    maybeEndByPrestige(state);
    return;
  }

  if (op.kind === 'lose_prestige') {
    const lost = Math.min(op.amount, Math.max(0, state.players[actor].prestige));
    addPrestige(state, actor, -lost);
    log(
      state,
      lost ? `${state.players[actor].name} lost ${lost} Prestige.` : `${state.players[actor].name} lost no Prestige.`
    );
    maybeEndByPrestige(state);
    return;
  }

  if (op.kind === 'steal_prestige') {
    if (prestigeIsShielded(state, foe)) {
      log(state, `${state.players[foe].name}'s Prestige is protected.`);
      return;
    }
    const stolen = Math.min(op.amount, state.players[foe].prestige);
    addPrestige(state, foe, -stolen);
    addPrestige(state, actor, stolen);
    log(
      state,
      stolen ? `${state.players[actor].name} stole ${stolen} Prestige.` : `${state.players[actor].name} stole nothing.`
    );
    maybeEndByPrestige(state);
    return;
  }

  if (op.kind === 'gain_stat') {
    if (op.stat) {
      applyGainStat(state, ctx, op.stat, op.amount, op.max);
      return;
    }
    const stats = [...BADGE_STAT_KEYS];
    if (!actorPicks(state, actor)) {
      applyGainStat(state, ctx, bestNextTurnBoostStat(state, actor, stats, op.amount), op.amount, op.max);
      return;
    }
    state.pending = { type: 'choose_stat', player: actor, stats, amount: op.amount, gain: true };
    state.phase = 'choice';
    return;
  }

  if (op.kind === 'lose_stat') {
    const who = state.players[actor];
    const current = cardStatGain(state, ctx.sourceCardId) ?? zeroStats();
    const have = cardStats(state, ctx.sourceCardId)[op.stat] + current[op.stat];
    const lost = Math.min(op.amount, Math.max(0, have));
    if (!lost) {
      log(state, `${BADGE_STAT_LABELS[op.stat]} is already at 0.`);
      return;
    }
    setCardStatGain(state, ctx.sourceCardId, { ...current, [op.stat]: current[op.stat] - lost });
    pushStatFx(state, actor, ctx.sourceCardId, op.stat, -lost);
    log(state, `${who.name} lost ${lost} ${BADGE_STAT_LABELS[op.stat]}.`);
    return;
  }

  if (op.kind === 'opposite_gain_stat') {
    const target = ctx.sourcePile == null ? null : pileCard(state.players[foe], ctx.sourcePile);
    if (!target) {
      log(state, `${state.players[foe].name}'s matching pile is empty.`);
      return;
    }
    const current = cardStatGain(state, target) ?? zeroStats();
    const room = Math.max(0, BADGE_STAT_MAX - cardStats(state, target)[op.stat] - current[op.stat]);
    const added = Math.min(op.amount, room);
    if (!added) {
      log(state, `${cardName(state, target)}'s ${BADGE_STAT_LABELS[op.stat]} is already at ${BADGE_STAT_MAX}.`);
      return;
    }
    setCardStatGain(state, target, { ...current, [op.stat]: current[op.stat] + added });
    pushStatFx(state, foe, target, op.stat, added);
    log(
      state,
      `${state.players[foe].name}'s ${cardName(state, target)} gained ${added} ${BADGE_STAT_LABELS[op.stat]}.`
    );
    return;
  }

  if (op.kind === 'reveal_unrepresented_drawn') {
    const present = representedRarities(state, state.players[actor]);
    ctx.playAfterReveal = false;
    ctx.revealPlayer = actor;
    ctx.revealQueue = ctx.lastDrawn.filter(
      (cardId) => state.players[actor].hand.includes(cardId) && !present.has(cardRarity(state, cardId))
    );
    processRevealQueue(state, ctx);
    return;
  }

  if (op.kind === 'swap_random_hand') {
    for (let i = 0; i < op.count; i += 1) {
      const a = randomHandCard(state, actor);
      const b = randomHandCard(state, foe);
      if (a && b) {
        takeFromHand(state.players[actor], a);
        takeFromHand(state.players[foe], b);
        receiveInHand(state, actor, b);
        receiveInHand(state, foe, a);
        pushFx(state, 'exchange', actor, b);
        pushFx(state, 'exchange', foe, a);
        log(state, `${state.players[actor].name} exchanged a badge with ${state.players[foe].name}.`);
        continue;
      }
      if (!a && b) {
        takeFromHand(state.players[foe], b);
        receiveInHand(state, actor, b);
        log(state, `${state.players[actor].name} stole a badge.`);
      }
      break;
    }
    return;
  }

  if (op.kind === 'swap_hand') {
    const given = state.players[actor].hand.filter((cardId) => matchesFilter(state, cardId, op.give));
    const taken = state.players[foe].hand.filter((cardId) => matchesFilter(state, cardId, op.take));
    given.forEach((cardId) => takeFromHand(state.players[actor], cardId));
    taken.forEach((cardId) => takeFromHand(state.players[foe], cardId));
    taken.forEach((cardId) => {
      if (receiveInHand(state, actor, cardId)) pushFx(state, 'exchange', actor, cardId);
    });
    given.forEach((cardId) => {
      if (receiveInHand(state, foe, cardId)) pushFx(state, 'exchange', foe, cardId);
    });
    const name = state.players[actor].name;
    if (!given.length && !taken.length) log(state, `${name} exchanged nothing.`);
    else log(state, `${name} exchanged ${given.length} badge${given.length === 1 ? '' : 's'} for ${taken.length}.`);
    return;
  }

  if (op.kind === 'reveal_unique_filter_drawn') {
    const selfHits = ctx.lastDrawn.filter(
      (cardId) => state.players[actor].hand.includes(cardId) && matchesFilter(state, cardId, op.filter)
    );
    const foeHits = ctx.lastDrawnOther.filter(
      (cardId) => state.players[foe].hand.includes(cardId) && matchesFilter(state, cardId, op.filter)
    );
    ctx.playAfterReveal = op.play ?? false;
    if (selfHits.length === 1 && foeHits.length === 0) {
      ctx.revealPlayer = actor;
      ctx.revealQueue = selfHits;
    } else if (selfHits.length === 0 && foeHits.length === 1) {
      ctx.revealPlayer = foe;
      ctx.revealQueue = foeHits;
    } else {
      ctx.revealQueue = [];
    }
    processRevealQueue(state, ctx);
    return;
  }

  if (op.kind === 'deck_bottom_drawn') {
    const remaining = ctx.lastDrawn.filter((cardId) => state.players[actor].hand.includes(cardId));
    const count = op.count ?? remaining.length;
    if (!remaining.length || count <= 0) return;
    const picking = remaining.length > count;
    if (picking && actorPicks(state, actor)) {
      state.pending = {
        type: 'choose_hand',
        player: actor,
        cardIds: remaining,
        count,
        purpose: 'bottom',
      };
      state.phase = 'choice';
      return;
    }
    const ordered = picking
      ? [...remaining].sort(
          (a, b) =>
            BADGE_STAT_KEYS.reduce((sum, key) => sum + cardStats(state, a)[key], 0) -
            BADGE_STAT_KEYS.reduce((sum, key) => sum + cardStats(state, b)[key], 0)
        )
      : remaining;
    ordered.slice(0, count).forEach((cardId) => bottomDrawnCard(state, actor, cardId));
    return;
  }

  if (op.kind === 'bounce_opponent_matching_drawn') {
    const drawn = lastDrawnCard(ctx);
    const to = op.to ?? 'hand';
    if (!drawn) return;
    requestBounceBoardBadge(
      state,
      actor,
      foe,
      bounceablePiles(state, foe, { kind: 'rarity', rarity: cardRarity(state, drawn) }),
      'bounce',
      to,
      "No matching badge on your opponent's board to return."
    );
    return;
  }

  if (op.kind === 'play_other') {
    requestPlayOther(state, ctx, op.count);
    return;
  }

  if (op.kind === 'bounce_source') {
    const pile = ctx.sourcePile;
    if (pile == null || pileCard(state.players[actor], pile) !== ctx.sourceCardId) {
      log(state, 'This badge is no longer on the board.');
      return;
    }
    bounceBoardBadge(state, actor, pile, op.to ?? 'hand');
    return;
  }

  if (op.kind === 'discard_source') {
    const pile = ctx.sourcePile;
    if (pile == null || pileCard(state.players[actor], pile) !== ctx.sourceCardId) {
      log(state, 'This badge is no longer on the board.');
      return;
    }
    discardOwnBoardBadge(state, actor, pile);
    return;
  }

  if (op.kind === 'move_source') {
    const piles = movePilesFor(state, ctx);
    if (!piles.length) {
      // The rest of the effect depends on the move, so it stops here.
      ctx.remainingOps = [];
      log(state, `No badge for ${cardName(state, ctx.sourceCardId)} to swap with.`);
      return;
    }
    if (piles.length === 1 && !actorPicks(state, actor)) {
      if (!moveSourceBadge(state, ctx, piles[0])) ctx.remainingOps = [];
      return;
    }
    state.pending = { type: 'choose_pile', player: actor, cardId: ctx.sourceCardId, piles, purpose: 'move' };
    state.phase = 'choice';
    return;
  }

  if (op.kind === 'swap_source_with_opponent_board') {
    const piles = opponentSwapPilesForSource(state, ctx);
    if (!piles.length) {
      // The rest of the effect depends on the swap, so it stops here.
      ctx.remainingOps = [];
      log(state, `No opponent badge for ${cardName(state, ctx.sourceCardId)} to swap with.`);
      return;
    }
    if (piles.length === 1 && !actorPicks(state, actor)) {
      if (!swapSourceWithOpponent(state, ctx, piles[0])) ctx.remainingOps = [];
      return;
    }
    state.pending = { type: 'choose_board_badge', player: actor, owner: foe, piles, purpose: 'swap_source' };
    state.phase = 'choice';
    return;
  }

  if (op.kind === 'discard_neighbor') {
    const neighbor =
      op.side === 'random'
        ? pickRandom(
            state,
            (['left', 'right'] as const)
              .map((side) => neighborPile(ctx.sourcePile, side, state))
              .filter((pile): pile is PileIndex => pile != null && !!livePileCard(state, state.players[actor], pile))
          )
        : neighborPile(ctx.sourcePile, op.side, state);
    if (neighbor == null || !livePileCard(state, state.players[actor], neighbor)) return;
    const cardId = takeFromPile(state.players[actor], neighbor);
    if (cardId) discardReplacedBadge(state, actor, cardId, true, { pile: neighbor });
    return;
  }

  if (op.kind === 'discard_board') {
    const removed: string[] = [];
    ([0, 1, 2, 3] as PileIndex[]).forEach((pile) => {
      const cardId = takeFromPile(state.players[actor], pile);
      if (cardId) removed.push(cardId);
    });
    // The source badge lands last so it tops the discard pile.
    const sourceIndex = removed.indexOf(ctx.sourceCardId);
    if (sourceIndex >= 0) removed.push(...removed.splice(sourceIndex, 1));
    removed.forEach((cardId) => discardReplacedBadge(state, actor, cardId, true));
    return;
  }

  if (op.kind === 'discard_board_badges') {
    ([actor, foe] as PlayerId[]).forEach((owner) => {
      bounceablePiles(state, owner, op.filter).forEach((pile) => {
        const cardId = pileCard(state.players[owner], pile);
        displacePile(state, owner, pile, 'discard');
        if (!cardId || pileCard(state.players[owner], pile) === cardId) return;
        const ctx = state.stack[state.stack.length - 1];
        if (ctx) {
          ctx.lastDiscarded[owner] = cardId;
          ctx.discardedCount[owner] += 1;
        }
      });
    });
    return;
  }

  if (op.kind === 'discard_revealed') {
    for (let i = 0; i < op.count; i++) {
      const piles = ([0, 1, 2, 3] as PileIndex[]).filter((pile) => {
        const cardId = livePileCard(state, state.players[actor], pile);
        return !!cardId && (!op.filter || matchesFilter(state, cardId, op.filter));
      });
      const pile = pickRandom(state, piles);
      if (pile == null) return;
      const cardId = takeFromPile(state.players[actor], pile);
      if (cardId) discardReplacedBadge(state, actor, cardId, true, { pile });
    }
    return;
  }

  if (op.kind === 'discard_self_board_badge') {
    const piles = bounceablePiles(state, actor);
    if (!piles.length) {
      log(state, 'No badge on your board to discard.');
      return;
    }
    if (piles.length === 1 && !actorPicks(state, actor)) {
      discardOwnBoardBadge(state, actor, piles[0], true);
      return;
    }
    state.pending = {
      type: 'choose_board_badge',
      player: actor,
      owner: actor,
      piles,
      purpose: 'discard_self',
    };
    state.phase = 'choice';
    return;
  }

  if (op.kind === 'replace_neighbor_from_deck') {
    const neighbor = neighborPile(ctx.sourcePile, op.side, state);
    const current = neighbor == null ? null : livePileCard(state, state.players[actor], neighbor);
    const higher = current ? rarityHigherBy(cardRarity(state, current), op.rarityOffset) : null;
    const candidates =
      neighbor == null || !current || !higher
        ? []
        : state.players[actor].deck.filter(
            (cardId) => cardRarity(state, cardId) === higher && isCardLegalForPile(state, cardId, neighbor, state.round)
          );
    const chosen = pickRandom(state, candidates);
    if (neighbor == null || !current) {
      log(state, `No badge to the ${op.side} to upgrade.`);
      return;
    }
    if (!chosen || !takeFromDeck(state.players[actor], chosen)) {
      log(
        state,
        op.rarityOffset === 1
          ? 'No legal badge one rarity higher can sit there.'
          : `No legal badge ${op.rarityOffset} rarities higher can sit there.`
      );
      return;
    }
    const replaced = replaceOwnBadge(state, actor, neighbor, chosen);
    if (replaced) discardReplacedBadge(state, actor, replaced, true, { replaced: true });
    return;
  }

  if (op.kind === 'replace_self_from_deck') {
    const pile = ctx.sourcePile;
    const sourceId = pile == null ? null : pileCard(state.players[actor], pile);
    const deck = state.players[actor].deck;
    const candidates =
      pile == null || sourceId !== ctx.sourceCardId
        ? []
        : deck.filter((cardId) => isCardLegalForPile(state, cardId, pile, state.round));
    const chosen = pickRandom(state, candidates);
    if (pile == null || !chosen || !takeFromDeck(state.players[actor], chosen)) {
      log(state, 'No badge in your deck can sit here.');
      if (op.play) ctx.activationMissed = true;
      return;
    }
    const replaced = replaceOwnBadge(state, actor, pile, chosen);
    if (replaced) discardReplacedBadge(state, actor, replaced, true, { replaced: true });
    if (op.play) {
      const incoming = pileCard(state.players[actor], pile);
      const incomingEffect = incoming ? cardEffects(state, incoming)[0] : undefined;
      if (incomingEffect?.trigger === 'Activate') {
        ctx.activationMissed = !startPlayingCard(state, actor, pile, false);
      }
    }
    return;
  }

  if (op.kind === 'shuffle_random_into_decks') {
    const pool = BATTLE_BADGE_IDS.filter((badgeId) => badgeRarity(badgeId) === op.rarity);
    const cards = (state.cards ??= {});
    let next = Object.keys(cards).length;
    [actor, foe].forEach((who) => {
      const badgeId = pickRandom(state, pool);
      if (!badgeId) return;
      while (cards[`#${next}`] != null) next++;
      const cardId = `#${next}`;
      cards[cardId] = badgeId;
      const deck = state.players[who].deck;
      const roll = nextUnit(state.rng);
      state.rng = roll.rng;
      deck.splice(Math.floor(roll.value * (deck.length + 1)), 0, cardId);
      pushFx(state, 'shuffle', who, cardId, { to: 'deck' });
      log(state, `${state.players[who].name} shuffled a ${RARITY_TIER_LABELS[op.rarity]} into their deck.`);
    });
    return;
  }

  if (op.kind === 'upgrade_pile_to_random') {
    const cardId = livePileCard(state, state.players[actor], op.pile);
    const badgeId = pickRandom(
      state,
      BATTLE_BADGE_IDS.filter((id) => badgeRarity(id) === op.into)
    );
    if (!cardId || cardRarity(state, cardId) !== op.rarity || !badgeId) {
      log(state, `No ${RARITY_TIER_LABELS[op.rarity]} on the ${pileLabel(op.pile)} pile to replace.`);
      return;
    }
    const replaced = replaceOwnBadge(state, actor, op.pile, spawnBadge(state, badgeId));
    if (replaced) discardReplacedBadge(state, actor, replaced, true, { pile: op.pile, replaced: true });
    return;
  }

  if (op.kind === 'replace_other_piles') {
    const pool = BATTLE_BADGE_IDS.filter((id) => badgeRarity(id) === op.rarity);
    ([actor, foe] as PlayerId[]).forEach((owner) => {
      ([0, 1, 2, 3] as PileIndex[]).forEach((pile) => {
        const current = livePileCard(state, state.players[owner], pile);
        if (!current || current === ctx.sourceCardId) return;
        if (opponentShields(state, owner)) {
          log(state, `${state.players[owner].name}'s ${cardName(state, current)} is protected.`);
          return;
        }
        const badgeId = pickRandom(state, pool);
        if (!badgeId) return;
        const replaced = replaceOwnBadge(state, owner, pile, spawnBadge(state, badgeId));
        if (replaced) discardReplacedBadge(state, owner, replaced, true, { pile, replaced: true });
      });
    });
    return;
  }

  if (op.kind === 'draw_rarity') {
    const player = state.players[actor];
    const cardId = pickRandom(
      state,
      player.deck.filter((id) => cardRarity(state, id) === op.rarity)
    );
    if (!cardId) {
      log(state, `${player.name} has no ${RARITY_TIER_LABELS[op.rarity]} in their deck.`);
      return;
    }
    takeFromDeck(player, cardId);
    if (receiveInHand(state, actor, cardId)) pushFx(state, 'draw', actor, cardId);
    ctx.lastDrawn = [cardId];
    logDraw(state, actor, [cardId]);
    return;
  }

  if (op.kind === 'reveal_from_decks') {
    [actor, foe].forEach((who) => {
      const player = state.players[who];
      const cardId = pickRandom(
        state,
        player.deck.filter((id) => cardRarity(state, id) === op.rarity)
      );
      if (!cardId) {
        log(state, `${player.name} has no ${RARITY_TIER_LABELS[op.rarity]} in their deck.`);
        return;
      }
      const legal = cardLegalPiles(state, cardId, state.round);
      const pile = legal.find((index) => player.piles[index] == null) ?? legal[legal.length - 1];
      if (pile == null) {
        log(state, `${player.name} cannot reveal a ${RARITY_TIER_LABELS[op.rarity]} from their deck.`);
        return;
      }
      takeFromDeck(player, cardId);
      tryRevealCard(state, who, cardId, pile, false);
    });
    return;
  }

  if (op.kind === 'exchange_self_with_opponent_hand') {
    const pile = ctx.sourcePile;
    const sourceId = pile == null ? null : pileCard(state.players[actor], pile);
    const candidates =
      pile == null || sourceId !== ctx.sourceCardId
        ? []
        : state.players[foe].hand.filter((cardId) => isCardLegalForPile(state, cardId, pile, state.round));
    const chosen = pickRandom(state, candidates);
    if (pile == null || !sourceId || !chosen || !takeFromHand(state.players[foe], chosen)) {
      log(state, "No badge in your opponent's hand can sit here.");
      return;
    }
    const replaced = takeFromPile(state.players[actor], pile);
    if (replaced) receiveInHand(state, foe, replaced);
    putOnPile(state.players[actor], chosen, pile);
    pushFx(state, 'exchange', actor, chosen, { pile });
    log(
      state,
      `${state.players[actor].name} exchanged ${cardName(state, replaced ?? sourceId)} for ${cardName(state, chosen)}.`,
      'reveal'
    );
    state.players[actor].revealedThisTurn.push(chosen);
    ctx.lastRevealed.push(chosen);
    startOnReveal(state, actor, pile);
    maybeActivateOnReveal(state, actor, pile);
    return;
  }

  if (op.kind === 'boost_stat') {
    const stats = op.stat
      ? [op.stat]
      : op.missing
        ? BADGE_STAT_KEYS.filter((stat) => cardStats(state, ctx.sourceCardId)[stat] <= 0)
        : [...BADGE_STAT_KEYS];
    if (!stats.length) {
      log(state, 'No missing stat to boost.');
      return;
    }
    if (stats.length === 1 || !actorPicks(state, actor)) {
      const stat = stats.length === 1 ? stats[0] : bestNextTurnBoostStat(state, actor, stats, op.amount);
      grantStatBoost(state, actor, stat, op.amount, ctx.sourceCardId);
      return;
    }
    state.pending = { type: 'choose_stat', player: actor, stats, amount: op.amount };
    state.phase = 'choice';
    return;
  }

  if (op.kind === 'discard_named_rarity') {
    const target = op.who === 'opponent' ? foe : actor;
    if (!state.players[target].hand.length) {
      log(state, `${state.players[target].name} has no badges in hand.`);
      return;
    }
    if (!actorPicks(state, actor)) {
      resolveNamedRarityDiscard(state, actor, guessRarityFromHand(state, state.players[target].hand));
      return;
    }
    state.pending = {
      type: 'choose_rarity',
      player: actor,
      rarities: [...RARITY_ORDER].reverse(),
      purpose: 'opponent_hand',
    };
    state.phase = 'choice';
    return;
  }

  if (op.kind === 'guess_drawn_rarity') {
    const deck = state.players[actor].deck;
    if (!deck.length) {
      log(state, 'Your deck is empty.');
      return;
    }
    if (!actorPicks(state, actor)) {
      const rarity = guessRarityFromDeck(state, deck);
      if (rarity) resolveDrawnRarityGuess(state, actor, rarity, op.count, op.reward);
      return;
    }
    state.pending = {
      type: 'choose_rarity',
      player: actor,
      rarities: [...RARITY_ORDER].reverse(),
      purpose: 'drawn',
      count: op.count,
      reward: op.reward,
    };
    state.phase = 'choice';
    return;
  }

  if (op.kind === 'scry_draw') {
    const peeked = state.players[actor].deck.slice(0, op.count);
    if (!peeked.length) {
      log(state, 'Your deck is empty.');
      return;
    }
    if (peeked.length === 1 || !actorPicks(state, actor)) {
      const chosen = peeked.length === 1 ? peeked[0] : (bestScryCard(state, peeked) ?? peeked[0]);
      drawPeekedCard(state, actor, peeked, chosen);
      return;
    }
    state.pending = { type: 'choose_deck_card', player: actor, cardIds: peeked };
    state.phase = 'choice';
    return;
  }

  if (op.kind === 'shield_badges') {
    state.players[actor].badgeShield = true;
    log(state, `${state.players[actor].name}'s badges are protected until their next turn.`);
    return;
  }

  if (op.kind === 'shield_prestige') {
    state.players[actor].prestigeShield = true;
    log(state, `${state.players[actor].name}'s Prestige is protected until their next turn.`);
    return;
  }

  if (op.kind === 'offer_swap_drawn') {
    const selfCard = ctx.lastDrawn[0];
    const foeCard = ctx.lastDrawnOther[0];
    const foe = opponentOf(actor);
    if (
      !selfCard ||
      !foeCard ||
      !state.players[actor].hand.includes(selfCard) ||
      !state.players[foe].hand.includes(foeCard)
    ) {
      log(state, 'No pair of drawn badges to exchange.');
      return;
    }
    if (!actorPicks(state, actor)) {
      const value = (cardId: string): number =>
        BADGE_STAT_KEYS.reduce((sum, key) => sum + cardStats(state, cardId)[key], 0) -
        rarityOrderIndex(cardRarity(state, cardId));
      if (value(foeCard) > value(selfCard)) swapDrawnCards(state, actor, selfCard, foeCard);
      return;
    }
    state.pending = { type: 'choose_swap_drawn', player: actor, selfCard, foeCard };
    state.phase = 'choice';
    return;
  }

  if (op.kind === 'bounce_self_board_badge') {
    const piles = bounceablePiles(state, actor, op.filter, op.excludeSource ? ctx.sourceCardId : undefined);
    if (op.random) {
      const pile = pickRandom(state, piles);
      if (pile == null) log(state, 'No badge on your board to return.');
      else bounceBoardBadge(state, actor, pile, op.to ?? 'hand', op.who === 'opponent' ? foe : undefined);
      return;
    }
    requestBounceBoardBadge(
      state,
      actor,
      actor,
      piles,
      'bounce_self',
      op.to ?? 'hand',
      op.who === 'opponent' ? 'No badge on your board to return.' : 'No badge on the board to return.',
      op.who
    );
    return;
  }

  if (op.kind === 'bounce_opponent_board_badge') {
    requestBounceBoardBadge(
      state,
      actor,
      foe,
      bounceablePiles(state, foe, op.filter),
      'bounce',
      op.to ?? 'hand',
      "No badge on your opponent's board to return."
    );
    return;
  }
};

const actorPicks = (state: BattleState, actor: PlayerId): boolean => !cpuDrives(state, actor);

const requestBounceBoardBadge = (
  state: BattleState,
  actor: PlayerId,
  owner: PlayerId,
  piles: PileIndex[],
  purpose: 'bounce' | 'bounce_self',
  to: BounceDest,
  emptyLog: string,
  who?: 'self' | 'opponent'
): void => {
  const receiver = who === 'opponent' ? opponentOf(owner) : undefined;
  if (!piles.length) {
    log(state, emptyLog);
    return;
  }
  if (piles.length === 1 && !actorPicks(state, actor)) {
    bounceBoardBadge(state, owner, piles[0], to, receiver, true);
    return;
  }
  state.pending = {
    type: 'choose_board_badge',
    player: actor,
    owner,
    piles,
    purpose,
    to,
    ...(who === 'opponent' ? { who } : {}),
  };
  state.phase = 'choice';
};

const playablePiles = (state: BattleState, actor: PlayerId, excludeIds: string[]): PileIndex[] =>
  liveBoardBadges(state, state.players[actor])
    .filter(
      ({ cardId }) =>
        !excludeIds.includes(cardId) &&
        cardEffects(state, cardId).some(
          (effect) => effect.stat != null && effect.trigger === 'Activate' && canActivateStat(state, actor, effect.stat)
        )
    )
    .map(({ pile }) => pile);

const requestPlayOther = (state: BattleState, ctx: BattleContinuation, add: number): void => {
  ctx.playOtherLeft += add;
  const piles = playablePiles(state, ctx.actor, ctx.playedThisChain);
  if (!piles.length) {
    ctx.playOtherLeft = 0;
    log(state, 'No other badge on the board to activate.');
    return;
  }
  if (piles.length === 1 && !actorPicks(state, ctx.actor)) {
    const cardId = pileCard(state.players[ctx.actor], piles[0]);
    if (cardId) ctx.playedThisChain.push(cardId);
    ctx.playOtherLeft -= 1;
    startPlayingCard(state, ctx.actor, piles[0], false);
    return;
  }
  state.pending = {
    type: 'choose_board_badge',
    player: ctx.actor,
    owner: ctx.actor,
    piles,
    purpose: 'play_other',
  };
  state.phase = 'choice';
};

const bounceBoardBadge = (
  state: BattleState,
  owner: PlayerId,
  pile: PileIndex,
  to: BounceDest = 'hand',
  receiver?: PlayerId,
  picked?: true
): void => {
  const before = pileCard(state.players[owner], pile);
  displacePile(state, owner, pile, to, true, receiver, picked);
  const ctx = state.stack[state.stack.length - 1];
  if (ctx && before && pileCard(state.players[owner], pile) !== before && owner === ctx.actor)
    ctx.returnedOwnBadge = true;
};

/** Neighbor Ongoing Prestige is held until this Activate finishes, so undo never keeps it. */
const grantNeighborActivatePrestige = (state: BattleState, ctx: BattleContinuation): void => {
  const bonuses = ctx.neighborBonuses;
  if (!bonuses?.length) return;
  ctx.neighborBonuses = [];
  const name = state.players[ctx.actor].name;
  for (const bonus of bonuses) {
    addPrestige(state, ctx.actor, bonus.amount);
    log(state, `${name} gained ${bonus.amount} Prestige from ${cardName(state, bonus.cardId)}.`);
  }
};

const swapDrawnCards = (state: BattleState, actor: PlayerId, selfCard: string, foeCard: string): void => {
  const foe = opponentOf(actor);
  if (!takeFromHand(state.players[actor], selfCard) || !takeFromHand(state.players[foe], foeCard)) return;
  receiveInHand(state, actor, foeCard);
  receiveInHand(state, foe, selfCard);
  pushFx(state, 'exchange', actor, foeCard);
  pushFx(state, 'exchange', foe, selfCard);
  log(state, `${state.players[actor].name} exchanged ${cardName(state, selfCard)} for ${cardName(state, foeCard)}.`);
};

const giveCard = (state: BattleState, from: PlayerId, cardId: string): void => {
  if (!takeFromHand(state.players[from], cardId)) return;
  const to = opponentOf(from);
  log(state, `${state.players[from].name} gave ${cardName(state, cardId)} to ${state.players[to].name}.`);
  if (receiveInHand(state, to, cardId)) pushFx(state, 'return', to, cardId, { to: 'hand' });
};

const pumpStack = (state: BattleState): void => {
  while (!state.pending && state.stack.length && state.phase !== 'game_over') {
    const ctx = state.stack[state.stack.length - 1];
    if (ctx.revealQueue.length) {
      processRevealQueue(state, ctx);
      continue;
    }
    if (ctx.remainingOps.length) {
      const op = ctx.remainingOps.shift() as BattleOp;
      applyOp(state, ctx, op);
      continue;
    }
    if (ctx.playOtherLeft > 0) {
      requestPlayOther(state, ctx, 0);
      if (state.pending || state.stack[state.stack.length - 1] !== ctx) continue;
      ctx.playOtherLeft = 0;
      continue;
    }
    grantNeighborActivatePrestige(state, ctx);
    state.stack.pop();
  }
  if (!state.pending && !state.stack.length) maybeEndByPrestige(state);
  if (!state.pending && !state.stack.length && state.phase !== 'game_over') {
    state.phase = 'turn';
    if (state.actionsLeft <= 0) finishTurn(state);
    else passTurnIfNoAction(state);
  }
  maybeAutoConcede(state);
};

const finishTurn = (state: BattleState): void => {
  const closing = state.players[state.activePlayer];
  if (closing.statBoostTurnsLeft > 1) closing.statBoostTurnsLeft -= 1;
  else {
    closing.statBoost = zeroStats();
    closing.statBoostCardId = null;
    closing.statBoostTurnsLeft = 0;
  }
  const closingSecondTurn = state.turnsTakenThisRound > 0;
  if (maybeEndByPrestige(state, closingSecondTurn ? 'turn_end' : 'gain')) return;
  state.turnsTakenThisRound += 1;
  if (state.turnsTakenThisRound >= 2) {
    if (state.players.every((seat) => (seat.actionsTaken ?? 0) === 0)) {
      endBattle(state);
      return;
    }
    if (state.round >= BATTLE_MAX_ROUND) {
      endBattle(state);
      return;
    }
    advanceRound(state);
    return;
  }
  startPlayerTurn(state, opponentOf(state.activePlayer));
};

const startPlayerTurn = (state: BattleState, player: PlayerId): void => {
  state.activePlayer = player;
  const who = state.players[player];
  who.badgeShield = false;
  who.prestigeShield = false;
  who.revealedThisTurn = [];
  who.playedThisTurn = [];
  who.actionsTaken = 0;
  const skip = who.skipActions;
  const extra = who.extraActions ?? 0;
  who.skipActions = 0;
  who.extraActions = 0;
  state.actionsLeft = Math.max(0, BATTLE_ACTIONS_PER_TURN - skip + extra);
  state.phase = 'turn';
  if (skip) {
    log(
      state,
      skip === 1
        ? `${state.players[player].name} skipped an action this turn.`
        : `${state.players[player].name} skipped ${skip} actions.`
    );
  }
  if (extra) {
    const taking = BATTLE_ACTIONS_PER_TURN + extra;
    log(state, `${state.players[player].name} takes ${taking} action${taking === 1 ? '' : 's'} this turn.`);
  }
  if (maybeEndByPrestige(state)) return;
  if (state.actionsLeft <= 0) finishTurn(state);
  else passTurnIfNoAction(state);
  maybeAutoConcede(state);
};

/** Nothing can be drawn, revealed, or activated, so the rest of this turn is skipped. */
const passTurnIfNoAction = (state: BattleState): void => {
  if (state.phase !== 'turn' || state.pending || state.stack.length || state.actionsLeft <= 0) return;
  if (hasTurnAction(state)) return;
  log(state, `${state.players[state.activePlayer].name} has no legal action and skips the turn.`);
  state.actionsLeft = 0;
  finishTurn(state);
};

const advanceRound = (state: BattleState): void => {
  state.round += 1;
  state.turnsTakenThisRound = 0;
  log(state, `Turn ${state.round}.`, 'round');
  startPlayerTurn(state, state.openingPlayer ?? 0);
};

const resolvePending = (state: BattleState, target: BattleTarget): boolean => {
  const pending = state.pending;
  if (!pending) return false;
  const ctx = state.stack[state.stack.length - 1];

  if (pending.type === 'choose_pile' && target.kind === 'pile') {
    if (!pending.piles.includes(target.pile)) return false;
    state.pending = null;
    if (pending.purpose === 'move') {
      // Prestige and later ops only count after a real swap.
      if (ctx && !moveSourceBadge(state, ctx, target.pile)) ctx.remainingOps = [];
      return true;
    }
    const placed = tryRevealCard(state, pending.player, pending.cardId, target.pile);
    if (ctx) ctx.revealQueue = ctx.revealQueue.filter((id) => id !== pending.cardId);
    if (placed && ctx?.playAfterReveal) startPlayingCard(state, pending.player, target.pile, false);
    return true;
  }

  if (pending.type === 'choose_pile' && target.kind === 'hand' && pending.options?.includes(target.cardId)) {
    // Clicking the picked badge undoes the pick; clicking another option switches to it.
    const options = pending.options;
    if (target.cardId === pending.cardId) {
      if (ctx) ctx.revealQueue = [];
      state.pending = { type: 'choose_hand', player: pending.player, cardIds: options, count: 1, purpose: 'reveal' };
      return true;
    }
    state.pending = null;
    if (ctx) pickRevealFromHand(state, ctx, target.cardId, options);
    return true;
  }

  if (pending.type === 'choose_hand' && target.kind === 'hand') {
    if (!pending.cardIds.includes(target.cardId)) return false;
    if (pending.purpose === 'reveal') {
      if (!state.players[pending.player].hand.includes(target.cardId)) return false;
      state.pending = null;
      if (ctx) pickRevealFromHand(state, ctx, target.cardId, pending.cardIds);
      return true;
    }
    if (pending.purpose === 'bottom') {
      if (!state.players[pending.player].hand.includes(target.cardId)) return false;
      bottomDrawnCard(state, pending.player, target.cardId);
      const remaining = pending.count - 1;
      if (remaining <= 0) {
        state.pending = null;
        return true;
      }
      const cardIds = pending.cardIds.filter(
        (id) => id !== target.cardId && state.players[pending.player].hand.includes(id)
      );
      if (!cardIds.length) {
        state.pending = null;
        return true;
      }
      state.pending = { ...pending, cardIds, count: Math.min(remaining, cardIds.length) };
      return true;
    }
    if (pending.purpose === 'give') {
      if (!state.players[pending.player].hand.includes(target.cardId)) return false;
      giveCard(state, pending.player, target.cardId);
      const remaining = pending.count - 1;
      if (remaining <= 0) {
        state.pending = null;
        return true;
      }
      const cardIds = pending.cardIds.filter(
        (id) => id !== target.cardId && state.players[pending.player].hand.includes(id)
      );
      if (!cardIds.length) {
        state.pending = null;
        return true;
      }
      state.pending = { ...pending, cardIds, count: Math.min(remaining, cardIds.length) };
      return true;
    }
    discardCard(state, pending.player, target.cardId, '');
    const remaining = pending.count - 1;
    if (remaining <= 0) {
      state.pending = null;
      return true;
    }
    const cardIds = pending.cardIds.filter(
      (id) => id !== target.cardId && state.players[pending.player].hand.includes(id)
    );
    if (!cardIds.length) {
      state.pending = null;
      return true;
    }
    state.pending = { ...pending, cardIds, count: Math.min(remaining, cardIds.length) };
    return true;
  }

  if (pending.type === 'choose_rarity' && target.kind === 'rarity') {
    if (!pending.rarities.includes(target.rarity)) return false;
    const drawn = pending.purpose === 'drawn' ? pending : null;
    state.pending = null;
    if (drawn) resolveDrawnRarityGuess(state, drawn.player, target.rarity, drawn.count, drawn.reward);
    else resolveNamedRarityDiscard(state, pending.player, target.rarity);
    return true;
  }

  if (pending.type === 'choose_swap_drawn' && target.kind === 'swap_drawn') {
    state.pending = null;
    if (target.swap) swapDrawnCards(state, pending.player, pending.selfCard, pending.foeCard);
    return true;
  }

  if (pending.type === 'choose_stat' && target.kind === 'stat') {
    if (!pending.stats.includes(target.stat)) return false;
    state.pending = null;
    if (pending.gain && ctx) {
      applyGainStat(state, ctx, target.stat, pending.amount);
      return true;
    }
    const source = state.stack[state.stack.length - 1]?.sourceCardId ?? '';
    grantStatBoost(state, pending.player, target.stat, pending.amount, source);
    return true;
  }

  if (pending.type === 'choose_deck_card' && target.kind === 'deck_card') {
    if (!pending.cardIds.includes(target.cardId)) return false;
    state.pending = null;
    drawPeekedCard(state, pending.player, pending.cardIds, target.cardId);
    return true;
  }

  if (pending.type === 'choose_board_badge' && target.kind === 'board_badge') {
    if (target.owner !== pending.owner || !pending.piles.includes(target.pile)) return false;
    state.pending = null;
    if (pending.purpose === 'discard_self') {
      discardOwnBoardBadge(state, pending.owner, target.pile, true);
      return true;
    }
    if (pending.purpose === 'bounce' || pending.purpose === 'bounce_self') {
      const receiver = pending.who === 'opponent' ? opponentOf(pending.owner) : undefined;
      bounceBoardBadge(state, pending.owner, target.pile, pending.to ?? 'hand', receiver, true);
      return true;
    }
    if (pending.purpose === 'swap_source') {
      if (!ctx) return false;
      if (!swapSourceWithOpponent(state, ctx, target.pile)) ctx.remainingOps = [];
      return true;
    }
    if (pending.purpose === 'play_other') {
      if (!ctx) return false;
      const cardId = pileCard(state.players[pending.owner], target.pile);
      if (!cardId) return true;
      ctx.playedThisChain.push(cardId);
      ctx.playOtherLeft = Math.max(0, ctx.playOtherLeft - 1);
      startPlayingCard(state, pending.owner, target.pile, false);
      return true;
    }
    return true;
  }

  return false;
};

export type CreateBattleArgs = {
  decks: [string[], string[]];
  names?: [string, string];
  seed?: number;
  cpu?: PlayerId | null;
  /** Difficulty the CPU opponent plays at. Ignored in person vs person. */
  cpuDifficulty?: CpuDifficulty;
  /** Drive both seats with the CPU (simulations and tests). */
  autoplay?: boolean;
  shuffle?: boolean;
  /** Odd seeds open with seat 1, even seeds with seat 0. Seats stay put. */
  coinToss?: boolean;
  /** When set, this seat opens. Shuffle still follows `seed`. Overrides `coinToss`. */
  opening?: PlayerId;
  /** Host, then guest: each queued 12 was fully claimed when the battle started. */
  pure?: [boolean, boolean];
};

const openingPlayer = (seed: number, coinToss: boolean): PlayerId => {
  if (!coinToss) return 0;
  return ((seed || 1) % 2) as PlayerId;
};

/** `#` keeps instance ids apart from badge ids, which older saved states still use as card ids. */
const cardInstanceId = (index: number): string => `#${index}`;

export const createBattle = ({
  decks,
  names = ['You', 'CPU'],
  seed = 1,
  cpu = 1,
  cpuDifficulty,
  autoplay = false,
  shuffle = true,
  coinToss = false,
  opening,
  pure,
}: CreateBattleArgs): BattleState => {
  if (decks.some((deck) => deck.length !== BATTLE_DECK_SIZE)) {
    throw new Error(`Battle deck must contain exactly ${BATTLE_DECK_SIZE} eligible badges.`);
  }
  let rng = seed || 1;
  // Every card gets its own id, so copies of one badge (even across decks) never share state.
  const cards: Record<string, string> = {};
  const [ids0, ids1] = decks.map((deck, seat) =>
    deck.map((badgeId, index) => {
      const cardId = cardInstanceId(seat * BATTLE_DECK_SIZE + index);
      cards[cardId] = badgeId;
      return cardId;
    })
  );
  let deck0 = ids0;
  let deck1 = ids1;
  if (shuffle) {
    const a = shuffleInPlace(deck0, rng);
    deck0 = a.items;
    rng = a.rng;
    const b = shuffleInPlace(deck1, rng);
    deck1 = b.items;
    rng = b.rng;
  }
  const players: [BattlePlayer, BattlePlayer] = [
    emptyPlayer(names[0], deck0, pure?.[0]),
    emptyPlayer(names[1], deck1, pure?.[1]),
  ];
  const first = opening ?? openingPlayer(seed, coinToss);
  const second: PlayerId = first === 0 ? 1 : 0;
  const state: BattleState = {
    players,
    cards,
    statGain: {},
    activePlayer: first,
    openingPlayer: first,
    round: 1,
    actionsLeft: BATTLE_OPENING_ACTIONS,
    turnsTakenThisRound: 0,
    rng,
    pending: null,
    stack: [],
    phase: 'turn',
    log: [],
    winner: null,
    cpu,
    ...(cpu != null || autoplay ? { cpuDifficulty: cpuDifficulty ?? 'easy' } : {}),
    autoplay,
    fx: [],
  };
  drawFromDeck(state, first, BATTLE_STARTING_HAND, false);
  drawFromDeck(state, second, BATTLE_SECOND_STARTING_HAND, false);
  log(state, 'Turn 1.', 'round');
  log(state, `${players[first].name} goes first.`, 'round');
  passTurnIfNoAction(state);
  return state;
};

const internTutorialSide = (
  side: BattleTutorial['you'],
  name: string,
  cards: Record<string, string>,
  startIndex: number
): { player: BattlePlayer; next: number } => {
  let next = startIndex;
  const internList = (ids: string[]) =>
    ids.map((badgeId) => {
      const cardId = cardInstanceId(next);
      next += 1;
      cards[cardId] = badgeId;
      return cardId;
    });
  const player = emptyPlayer(name, internList(side.deck));
  player.hand = internList(side.hand);
  return { player, next };
};

/** A fresh battle with hands and deck order fixed, so a scripted CPU can play it the same way every time. */
export const createTutorialBattle = (
  tutorial: BattleTutorial,
  names: [string, string] = ['You', 'CPU'],
  seed = 1
): BattleState => {
  const cards: Record<string, string> = {};
  const you = internTutorialSide(tutorial.you, names[0], cards, 0);
  const cpu = internTutorialSide(tutorial.cpu, names[1], cards, you.next);
  const first = tutorialOpeningPlayer(tutorial);
  const state: BattleState = {
    players: [you.player, cpu.player],
    cards,
    statGain: {},
    activePlayer: first,
    openingPlayer: first,
    round: 1,
    actionsLeft: BATTLE_OPENING_ACTIONS,
    turnsTakenThisRound: 0,
    rng: seed || 1,
    pending: null,
    stack: [],
    phase: 'turn',
    log: [],
    winner: null,
    cpu: 1,
    cpuDifficulty: tutorial.cpuDifficulty ?? 'easy',
    autoplay: false,
    fx: [],
    pileCount: tutorial.piles ?? BATTLE_PILE_COUNT,
  };
  log(state, 'Turn 1.', 'round');
  log(state, `${names[first]} goes first.`, 'round');
  passTurnIfNoAction(state);
  return state;
};

/**
 * A play can be taken back while it waits on its player and has only touched things they could redo:
 * no rng, no deck change, no new cards in hand (revealing from it is fine), and nothing on the opponent's side.
 */
const canUndoTo = (before: BattleState, after: BattleState, player: PlayerId): boolean => {
  if (after.phase !== 'choice' || after.pending?.player !== player || after.rng !== before.rng) return false;
  const mine = before.players[player];
  const mineNow = after.players[player];
  const foe = opponentOf(player);
  const handLeft = [...mine.hand];
  const onlyRevealed = mineNow.hand.every((cardId) => {
    const index = handLeft.indexOf(cardId);
    if (index < 0) return false;
    handLeft.splice(index, 1);
    return true;
  });
  return (
    onlyRevealed &&
    JSON.stringify(mine.deck) === JSON.stringify(mineNow.deck) &&
    JSON.stringify(before.players[foe]) === JSON.stringify(after.players[foe])
  );
};

export const reduceBattle = (inputState: BattleState, input: BattleInput): BattleState => {
  if (inputState.phase === 'game_over') return inputState;

  if (input.type === 'undo') {
    const undo = inputState.undo;
    return undo ? { ...undo.state, fx: [], undo: null } : inputState;
  }

  const state = cloneState(inputState);
  state.fx = [];
  const undo = inputState.undo ?? null;
  state.undo = null;

  maybeAutoConcede(state);
  if (state.phase === 'game_over') return state;

  if (input.type === 'resolve') {
    if (state.phase !== 'choice' || !state.pending) return inputState;
    if (!resolvePending(state, input.target)) return inputState;
    pumpStack(state);
    if (undo && canUndoTo(undo.state, state, undo.player)) state.undo = undo;
    return state;
  }

  if (state.phase === 'choice') return inputState;
  if (state.actionsLeft <= 0) return inputState;

  if (input.type === 'pass') {
    if (hasTurnAction(state)) return inputState;
    passTurnIfNoAction(state);
    maybeAutoConcede(state);
    return state;
  }

  if (input.type === 'draw') {
    if (!state.players[state.activePlayer].deck.length) return inputState;
    const drawn = drawFromDeck(state, state.activePlayer, 1);
    state.actionsLeft -= 1;
    state.players[state.activePlayer].actionsTaken += 1;
    logDraw(state, state.activePlayer, drawn, 'draw');
    pumpStack(state);
    return state;
  }

  if (input.type === 'reveal') {
    const piles = cardLegalPiles(state, input.cardId, state.round);
    if (!piles.includes(input.pile)) return inputState;
    if (!state.players[state.activePlayer].hand.includes(input.cardId)) return inputState;
    if (!tryRevealCard(state, state.activePlayer, input.cardId, input.pile)) return inputState;
    state.actionsLeft -= 1;
    state.players[state.activePlayer].actionsTaken += 1;
    pumpStack(state);
    const player = inputState.activePlayer;
    if (canUndoTo(inputState, state, player)) {
      state.undo = { player, pile: input.pile, state: { ...inputState, undo: null } };
    }
    return state;
  }

  if (input.type === 'play') {
    const cardId = pileCard(state.players[state.activePlayer], input.pile);
    if (!cardId) return inputState;
    if (!startPlayingCard(state, state.activePlayer, input.pile, true)) return inputState;
    pumpStack(state);
    const player = inputState.activePlayer;
    if (canUndoTo(inputState, state, player)) {
      state.undo = { player, pile: input.pile, state: { ...inputState, undo: null } };
    }
    return state;
  }

  return inputState;
};
